import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, CreditCard, Wallet, RefreshCw, Filter, Info,
  CornerUpLeft, ArrowLeftRight, Ban, Clock,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';

/* Read-only view of the Plaid transaction stream.
 *
 * Deliberately not a categorization workspace — Track C owns that. What this
 * screen is for is seeing what actually came in: which account a transaction
 * belongs to, what kind of movement it is, whether it is still pending, and
 * which rows are excluded from expense because booking them would count the
 * same spend twice. */

const CLASS_LABELS = {
  bank_debit:        { label: 'Bank debit',       tone: 'bg-slate-100 text-slate-700',   icon: Wallet },
  card_purchase:     { label: 'Card purchase',    tone: 'bg-violet-50 text-violet-700',  icon: CreditCard },
  refund:            { label: 'Refund',           tone: 'bg-emerald-50 text-emerald-700', icon: CornerUpLeft },
  deposit:           { label: 'Deposit',          tone: 'bg-teal-50 text-teal-700',      icon: Wallet },
  internal_transfer: { label: 'Internal transfer', tone: 'bg-sky-50 text-sky-700',       icon: ArrowLeftRight },
  card_payment:      { label: 'Card payment',     tone: 'bg-amber-50 text-amber-800',    icon: ArrowLeftRight },
  unknown:           { label: 'Unknown',          tone: 'bg-slate-100 text-slate-500',   icon: Info },
};

function ClassBadge({ txnClass, reason }) {
  const spec = CLASS_LABELS[txnClass] || CLASS_LABELS.unknown;
  const Icon = spec.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${spec.tone}`}
      title={reason || ''} data-testid={`class-${txnClass}`}>
      <Icon size={10} /> {spec.label}
    </span>
  );
}

export default function PlaidTransactions() {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({
    plaid_account_id: '', txn_class: '', state: '', include_excluded: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filters.plaid_account_id) params.set('plaid_account_id', filters.plaid_account_id);
      if (filters.txn_class)        params.set('txn_class', filters.txn_class);
      if (filters.state)            params.set('state', filters.state);
      if (!filters.include_excluded) params.set('include_excluded', 'false');

      const [txns, accts] = await Promise.all([
        api.get(`/api/accounts/plaid/transactions?${params}`),
        api.get('/api/accounts/plaid/accounts'),
      ]);
      setRows(txns.data.transactions || []);
      setTotal(txns.data.total || 0);
      setAccounts(accts.data || []);
    } catch {
      toast.error('Failed to load imported transactions');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setFilters(f => ({ ...f, [key]: value }));

  const money = (signed, currency) => {
    if (signed == null) return '—';
    const value = Number(signed);
    return (
      <span className={value < 0 ? 'text-slate-800' : 'text-emerald-700'}>
        {value < 0 ? '−' : '+'}
        {currency === 'USD' ? '$' : `${currency} `}
        {Math.abs(value).toLocaleString(undefined, {
          minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    );
  };

  const accountLabel = (plaidAccountId) => {
    const account = accounts.find(a => a.plaid_account_id === plaidAccountId);
    if (!account) return plaidAccountId ? '—' : '—';
    return `${account.name}${account.mask ? ` ••${account.mask}` : ''}`;
  };

  const excludedCount = rows.filter(r => r.exclude_from_expense).length;

  return (
    <div className="p-6 fade-in" data-testid="plaid-transactions-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <Link to="/accounts/usa/bank-connections"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-1">
            <ArrowLeft size={12} /> Bank Connections
          </Link>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Imported Transactions
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Everything Plaid has delivered, exactly as delivered. Showing {rows.length} of {total}.
          </p>
        </div>
        <button onClick={load} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50" title="Reload">
          <RefreshCw size={14} className={`text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
        <Filter size={13} className="text-slate-400" />
        <select value={filters.plaid_account_id} onChange={e => set('plaid_account_id', e.target.value)}
          className="text-xs border border-slate-200 rounded px-2 py-1.5" data-testid="filter-account">
          <option value="">All accounts</option>
          {accounts.map(a => (
            <option key={a.account_id} value={a.plaid_account_id}>
              {a.is_credit_card ? '💳 ' : '🏦 '}{a.name}{a.mask ? ` ••${a.mask}` : ''}
            </option>
          ))}
        </select>
        <select value={filters.txn_class} onChange={e => set('txn_class', e.target.value)}
          className="text-xs border border-slate-200 rounded px-2 py-1.5" data-testid="filter-class">
          <option value="">All types</option>
          {Object.entries(CLASS_LABELS).map(([value, spec]) => (
            <option key={value} value={value}>{spec.label}</option>
          ))}
        </select>
        <select value={filters.state} onChange={e => set('state', e.target.value)}
          className="text-xs border border-slate-200 rounded px-2 py-1.5" data-testid="filter-state">
          <option value="">Live (pending + posted)</option>
          <option value="pending">Pending only</option>
          <option value="posted">Posted only</option>
          <option value="superseded">Superseded by a settled version</option>
          <option value="removed">Removed by the bank</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={filters.include_excluded}
            onChange={e => set('include_excluded', e.target.checked)}
            data-testid="filter-excluded" />
          Include rows excluded from expense
        </label>
        {excludedCount > 0 && filters.include_excluded && (
          <span className="text-xs text-slate-400">
            {excludedCount} shown row(s) would double-count if booked
          </span>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Wallet size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-600 mb-1">Nothing imported yet</p>
            <p className="text-xs text-slate-400">
              Connect an institution and run a sync from Bank Connections.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="plaid-transactions-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Date', 'Description', 'Account', 'Type', 'Amount', 'State'].map(h => (
                    <th key={h} className={`px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                      h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(row => (
                  <tr key={row.txn_id}
                    className={`hover:bg-slate-50 ${row.exclude_from_expense ? 'bg-slate-50/50' : ''}`}
                    data-testid={`txn-${row.plaid_transaction_id}`}>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <p className="text-sm text-slate-700">{row.date || '—'}</p>
                      {row.authorized_date && row.authorized_date !== row.date && (
                        <p className="text-xs text-slate-400" title="Authorized date">
                          auth {row.authorized_date}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-sm text-slate-800">{row.merchant_name || row.name}</p>
                      {row.merchant_name && row.name !== row.merchant_name && (
                        <p className="text-xs text-slate-400 font-mono" title="Raw bank description">
                          {row.name}
                        </p>
                      )}
                      {row.exclude_from_expense && (
                        <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5">
                          <Ban size={10} /> {row.exclude_reason || 'Excluded from expense'}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {row.account_type === 'credit'
                          ? <CreditCard size={11} className="text-violet-500" />
                          : <Wallet size={11} className="text-sky-500" />}
                        {accountLabel(row.plaid_account_id)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <ClassBadge txnClass={row.txn_class} reason={row.class_reason} />
                    </td>
                    <td className="px-5 py-3 text-right text-sm tabular-nums whitespace-nowrap">
                      {money(row.signed_amount, row.currency)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {row.is_pending ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-800 text-xs rounded-full font-medium"
                          title="Not yet settled; the amount can still change">
                          <Clock size={10} /> Pending
                        </span>
                      ) : row.state === 'superseded' ? (
                        <span className="text-xs text-slate-400"
                          title="Replaced by its settled version">superseded</span>
                      ) : row.state === 'removed' ? (
                        <span className="text-xs text-red-500"
                          title="The institution withdrew this transaction">removed</span>
                      ) : (
                        <span className="text-xs text-slate-500">posted</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-slate-700 mb-2">Reading this screen</h3>
        <ul className="space-y-1 text-xs text-slate-500 list-disc list-inside">
          <li><strong>Amount</strong> is signed from Eyvo's point of view: negative leaves, positive arrives.</li>
          <li><strong>Type</strong> says what the movement is, not which GL account it belongs to. Hover it for the reason.</li>
          <li><strong>Pending</strong> rows can still change; they are replaced by their settled version rather than added to.</li>
          <li><strong>Excluded from expense</strong> marks rows that would double-count — a payment to an Eyvo card whose purchases are already imported, or a transfer between Eyvo accounts.</li>
        </ul>
      </div>
    </div>
  );
}
